import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HolidaysPage = () => {
  const { isHR } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newHoliday, setNewHoliday] = useState({ name: '', date: null, type: 'public' });

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/holidays?year=${selectedYear}`, { withCredentials: true });
      setHolidays(response.data);
    } catch (error) {
      console.error('Fetch holidays error:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date) {
      toast.error('Please select a date');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/holidays`, {
        ...newHoliday,
        date: format(newHoliday.date, 'yyyy-MM-dd')
      }, { withCredentials: true });
      toast.success('Holiday added');
      setIsAddDialogOpen(false);
      setNewHoliday({ name: '', date: null, type: 'public' });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/holidays/${id}`, { withCredentials: true });
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  const getTypeBadge = (type) => {
    const classes = { public: 'badge-success', optional: 'badge-warning', restricted: 'badge-ai' };
    return <Badge className={classes[type]}>{type}</Badge>;
  };

  // Group by month
  const holidaysByMonth = holidays.reduce((acc, h) => {
    const month = new Date(h.date).toLocaleDateString('en-IN', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(h);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="holidays-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-primary" />
            Holiday Calendar
          </h1>
          <p className="text-muted-foreground">View and manage company holidays</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            {isHR && (
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-holiday-btn"><Plus className="w-4 h-4" />Add Holiday</Button>
            </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader><DialogTitle>Add Holiday</DialogTitle></DialogHeader>
              <form onSubmit={handleAddHoliday} className="space-y-4">
                <div className="space-y-2">
                  <Label>Holiday Name</Label>
                  <Input value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} placeholder="e.g., Republic Day" required data-testid="holiday-name" />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !newHoliday.date && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newHoliday.date ? format(newHoliday.date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={newHoliday.date} onSelect={(date) => setNewHoliday({ ...newHoliday, date })} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={newHoliday.type} onValueChange={(v) => setNewHoliday({ ...newHoliday, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public Holiday</SelectItem>
                      <SelectItem value="optional">Optional Holiday</SelectItem>
                      <SelectItem value="restricted">Restricted Holiday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-holiday">Add</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Public Holidays</p><p className="text-2xl font-bold">{holidays.filter(h => h.type === 'public').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Optional Holidays</p><p className="text-2xl font-bold">{holidays.filter(h => h.type === 'optional').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Holidays</p><p className="text-2xl font-bold">{holidays.length}</p></CardContent></Card>
      </div>

      {/* Holidays by Month */}
      {Object.keys(holidaysByMonth).length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(holidaysByMonth).map(([month, monthHolidays]) => (
            <Card key={month}>
              <CardHeader><CardTitle className="text-lg">{month}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {monthHolidays.map((holiday) => {
                    const isPast = new Date(holiday.date) < new Date(new Date().toDateString());
                    return (
                    <div key={holiday.id} className={`flex items-center justify-between p-2 rounded-md ${isPast ? 'bg-red-100 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50' : 'bg-muted'}`} data-testid={`holiday-row-${holiday.id}`}>
                      <div>
                        <p className={`font-medium text-sm ${isPast ? 'text-red-800 dark:text-red-300 line-through decoration-red-400/50' : ''}`}>{holiday.name}</p>
                        <p className={`text-xs ${isPast ? 'text-red-600/70 dark:text-red-400/70' : 'text-muted-foreground'}`}>
                          {new Date(holiday.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' })}
                          {isPast && <span className="ml-1.5 italic">(past)</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getTypeBadge(holiday.type)}
                        {isHR && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteHoliday(holiday.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </div>
                  );})}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No holidays found for {selectedYear}</CardContent></Card>
      )}
    </div>
  );
};

export default HolidaysPage;
